import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiActivity,
  FiArrowLeft,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiInfo,
  FiList,
  FiPause,
  FiPlay,
  FiRefreshCw,
  FiRotateCcw,
  FiSend,
  FiStar,
  FiTrash2,
  FiUpload,
  FiUser,
  FiUserCheck,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import api from "../../libs/axios";
import { uploadFilesWithPresignedPayload } from "../../libs/presigned-upload";
import { useAuth } from "../../provider/auth.provider";
import { useConfirm } from "../../provider/confirm.provider";
import { SelectWithSearch, type SelectOption } from "../../utils/select-with-search";
import { Pagination, type PaginationType } from "../../utils/pagination.utils";
import {
  DESIGN_ACTIONS,
  DESIGN_BILLING_STATUSES,
  DESIGN_FILE_ACCEPT,
  DESIGN_FILE_STAGES,
  DESIGN_PRIORITIES,
  DESIGN_RESOURCE,
  DESIGN_SHARE_MODES,
  DESIGN_VENDOR_STATUSES,
  billingStatusLabel,
  fileStageFolder,
  formatAmount,
  formatDateOnly,
  formatDateTime,
  personName,
  priorityBadgeClass,
  priorityLabel,
  reportMakerStatusBadgeClass,
  reportMakerStatusLabel,
  reportTypeLabel,
  shareModeLabel,
  stageBadgeClass,
  stageLabel,
  vendorStatusLabel,
  workStatusBadgeClass,
  workStatusLabel,
} from "./design_constants";

/**
 * One Designing site.
 *
 * Everything on this page comes from GET /designing/sites/:id/workflow-status:
 * the site, its seven steps (who, when, remarks, files), the rejections so far,
 * and `available_actions` — the actions THIS user can take on THIS site right
 * now. The server works that list out from the user's permissions, the site's
 * stage and work status, and whether the user is the assigned report maker or
 * validator; the page only renders a button for each name it gets back, so the
 * portal and the API can never disagree about who may do what.
 */

// ── Action catalogue ─────────────────────────────────────────────────────────
// The buttons this page can show, keyed by the names in `available_actions`.
type FieldType =
  | "text"
  | "textarea"
  | "email"
  | "date"
  | "number"
  | "select"
  | "files"
  | "rating"
  | "user"
  | "vendor"
  | "billable"
  // The site's report makers: one row each — person + their work.
  | "report_makers"
  // Validator rejecting: tick which report makers get the report back.
  | "rework_pick"
  // Upload report: whose report this is (own report, or — super admin — pick).
  | "report_owner"
  // Validator: accept / send back each report maker's report.
  | "reviews";

interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  hint?: string;
  // For "user": which kind of assignee to list.
  assigneeKind?: "report_maker" | "validator";
}

interface ActionDef {
  label: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  // Tailwind classes of the header button.
  tone: string;
  submitLabel: string;
  fields: FieldDef[];
  // Where the files go in S3, when the action uploads any.
  folder?: string;
  // Extra check across fields; returns a message to block the submit.
  validate?: (values: Record<string, any>, files: File[]) => string | null;
  // Builds the request.
  request: (id: string, values: Record<string, any>, uploadedFiles: any[]) => { method: "post" | "put"; url: string; body: any };
}

const PRIMARY = "bg-blue-800 hover:bg-blue-900 text-white";
const DANGER = "bg-red-600 hover:bg-red-700 text-white";
const NEUTRAL = "border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white";
const WARN = "bg-amber-600 hover:bg-amber-700 text-white";
const SUCCESS = "bg-green-700 hover:bg-green-800 text-white";

const clean = (value: any) => (value === "" || value === undefined ? undefined : value);

// Site data and the finished report can both be handed over outside the portal,
// so the two upload steps share this rule: files, or a note of where it went.
const handoverCheck = (noun: string) => (values: Record<string, any>, files: File[]) => {
  const sharedVia = values.shared_via || "portal";
  if (files.length === 0 && sharedVia === "portal") {
    return `Upload at least one ${noun} file, or choose how the ${noun} was shared.`;
  }
  if (sharedVia === "drive_link" && !String(values.link || "").trim()) {
    return "Enter the drive link.";
  }
  return null;
};

const SHARE_FIELDS: FieldDef[] = [
  {
    name: "shared_via",
    label: "Shared via",
    type: "select",
    options: DESIGN_SHARE_MODES,
    hint: "Leave as “Uploaded on portal” when the files are attached here.",
  },
  { name: "link", label: "Link (optional)", type: "text", placeholder: "Drive link, or where it can be found" },
];

const assignFields = (kind: "report_maker" | "validator"): FieldDef[] => [
  {
    name: "user",
    label: kind === "report_maker" ? "Report Maker" : "Validator",
    type: "user",
    required: true,
    assigneeKind: kind,
    hint:
      kind === "report_maker"
        ? "Users on this site's project who can upload a report."
        : "Users on this site's project who can finalize a report. None of the site's report makers can validate the report.",
  },
  { name: "priority", label: "Priority", type: "select", options: [{ value: "", label: "Default (medium)" }, ...DESIGN_PRIORITIES] },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

// ── Report makers (several per site, each with their own work) ──────────────
interface ReportMakerRow {
  user: SelectOption | null;
  work: string;
}

const userOption = (u: any): SelectOption | null =>
  u ? { id: u.id, value: u.id, label: `${`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username}${u.employee_code ? ` (${u.employee_code})` : ""}` } : null;

// The editor starts from the site's current team, or one empty row.
const teamRows = (site: any): ReportMakerRow[] => {
  const team: any[] = site?.report_makers || [];
  return team.length
    ? team.map((m) => ({ user: userOption(m.user) || { id: m.user_id, value: m.user_id, label: `User ${m.user_id}` }, work: m.work_description || "" }))
    : [{ user: null, work: "" }];
};

const reportMakersBody = (rows: ReportMakerRow[] = []) =>
  rows
    .filter((row) => row.user)
    .map((row) => ({ user_id: row.user!.id, work_description: row.work.trim() || null }));

const reportMakersCheck = (values: Record<string, any>) => {
  const rows: ReportMakerRow[] = values.report_makers || [];
  const picked = rows.filter((row) => row.user);
  if (picked.length === 0) return "Choose at least one report maker.";
  if (rows.some((row) => !row.user && row.work.trim())) return "Choose a person for every row that has work written in it.";
  const ids = picked.map((row) => row.user!.id);
  if (new Set(ids).size !== ids.length) return "The same report maker is listed twice.";
  return null;
};

const reportMakerFields: FieldDef[] = [
  {
    name: "report_makers",
    label: "Report makers",
    type: "report_makers",
    required: true,
    hint: "Users on this site's project who can upload a report. Add as many as needed and write what each will do. Each one submits their own report; the site moves on once every report is in.",
  },
  { name: "priority", label: "Priority", type: "select", options: [{ value: "", label: "Default (medium)" }, ...DESIGN_PRIORITIES] },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

const assignReportMakersRequest = (id: string, v: Record<string, any>) => ({
  method: "post" as const,
  url: `/designing/sites/${id}/assign-report-maker`,
  body: { report_makers: reportMakersBody(v.report_makers), priority: clean(v.priority), remarks: clean(v.remarks) },
});

const assignRequest =
  (path: string) =>
  (id: string, v: Record<string, any>) => ({
    method: "post" as const,
    url: `/designing/sites/${id}/${path}`,
    body: { user_id: v.user?.id, priority: clean(v.priority), remarks: clean(v.remarks) },
  });

const ACTIONS: Record<string, ActionDef> = {
  upload_data: {
    label: "Upload Site Data",
    title: "Upload site data",
    description: "Attach the site data, or record where it was shared. The site moves to Site Data Uploaded.",
    icon: FiUpload,
    tone: PRIMARY,
    submitLabel: "Upload site data",
    folder: "designing/site-data",
    fields: [
      { name: "files", label: "Site data files", type: "files" },
      ...SHARE_FIELDS,
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    validate: handoverCheck("site data"),
    request: (id, v, uploadedFiles) => ({
      method: "post",
      url: `/designing/sites/${id}/upload-data`,
      body: { uploadedFiles, shared_via: v.shared_via || "portal", link: clean(v.link), remarks: clean(v.remarks) },
    }),
  },
  assign_report_maker: {
    label: "Assign Report Makers",
    title: "Assign report makers",
    description: "The site moves to Report Maker Assigned and each report maker is notified with their work.",
    icon: FiUserCheck,
    tone: PRIMARY,
    submitLabel: "Assign",
    fields: reportMakerFields,
    validate: reportMakersCheck,
    request: assignReportMakersRequest,
  },
  reassign_report_maker: {
    label: "Edit Report Makers",
    title: "Edit report makers",
    description:
      "Add or remove report makers, or change what each one does. Only people newly added are notified. The change is kept on the timeline.",
    icon: FiUserCheck,
    tone: NEUTRAL,
    submitLabel: "Save report makers",
    fields: reportMakerFields,
    validate: reportMakersCheck,
    request: assignReportMakersRequest,
  },
  reject_site_data: {
    label: "Reject Site Data",
    title: "Reject site data",
    description: "Sends the site back to its creator at Site Created. The creator is notified with your reason.",
    icon: FiXCircle,
    tone: DANGER,
    submitLabel: "Reject site data",
    fields: [
      { name: "rejection_reason", label: "Rejection reason", type: "textarea", required: true, placeholder: "What is incomplete or wrong in the site data?" },
      { name: "remarks", label: "Remarks", type: "textarea" },
      { name: "quality_feedback", label: "Quality feedback", type: "textarea" },
    ],
    request: (id, v) => ({
      method: "post",
      url: `/designing/sites/${id}/reject-site-data`,
      body: { rejection_reason: v.rejection_reason, remarks: clean(v.remarks), quality_feedback: clean(v.quality_feedback) },
    }),
  },
  upload_report: {
    label: "Submit My Report",
    title: "Submit report",
    description:
      "Attach your report, or record where it was submitted. Every report maker submits their own report; the site moves to Report Completed once all of them are in. You can replace your submission until the validator accepts it.",
    icon: FiUpload,
    tone: PRIMARY,
    submitLabel: "Submit report",
    folder: "designing/reports",
    fields: [
      { name: "report_maker_id", label: "Report of", type: "report_owner" },
      { name: "files", label: "Report files", type: "files" },
      ...SHARE_FIELDS,
      { name: "remarks", label: "Remarks", type: "textarea" },
      { name: "quality_feedback", label: "Quality feedback", type: "textarea", hint: "Feedback on the site data you were given." },
    ],
    validate: (v, files) =>
      v.report_owner_required && !v.report_maker_id ? "Choose whose report this is." : handoverCheck("report")(v, files),
    request: (id, v, uploadedFiles) => ({
      method: "post",
      url: `/designing/sites/${id}/upload-report`,
      body: {
        report_maker_id: v.report_maker_id || undefined,
        uploadedFiles,
        shared_via: v.shared_via || "portal",
        link: clean(v.link),
        remarks: clean(v.remarks),
        quality_feedback: clean(v.quality_feedback),
      },
    }),
  },
  assign_validator: {
    label: "Assign Validator",
    title: "Assign validator",
    description: "The site moves to Validator Assigned and the validator is notified.",
    icon: FiUserCheck,
    tone: PRIMARY,
    submitLabel: "Assign",
    fields: assignFields("validator"),
    request: assignRequest("assign-validator"),
  },
  reassign_validator: {
    label: "Change Validator",
    title: "Change validator",
    description: "Hands the report to a different validator. The change is kept on the timeline.",
    icon: FiUserCheck,
    tone: NEUTRAL,
    submitLabel: "Change validator",
    fields: assignFields("validator"),
    request: assignRequest("assign-validator"),
  },
  finalize_report: {
    label: "Finalize Report",
    title: "Finalize report",
    description:
      "Every report maker's report is accepted. Confirms the final report is correct; the site moves to Report Finalized, ready to be sent to the client.",
    icon: FiCheckCircle,
    tone: SUCCESS,
    submitLabel: "Finalize report",
    folder: "designing/validator-files",
    fields: [
      { name: "remarks", label: "Remarks", type: "textarea", required: true },
      { name: "qa_feedback", label: "QA feedback", type: "textarea" },
      { name: "rating", label: "Report maker rating", type: "rating" },
      { name: "files", label: "Upload files (optional)", type: "files" },
    ],
    request: (id, v, uploadedFiles) => ({
      method: "post",
      url: `/designing/sites/${id}/finalize-report`,
      body: { remarks: v.remarks, qa_feedback: clean(v.qa_feedback), rating: v.rating || undefined, uploadedFiles },
    }),
  },
  review_reports: {
    label: "Review Reports",
    title: "Review each report maker's report",
    description:
      "Accept or send back each report maker's report on its own — as many at once as you like. Accepted reports stay accepted. Only the report makers you send back get the site back, with your reason; it returns to you once they resubmit. The report can be finalized once every report is accepted.",
    icon: FiCheckSquare,
    tone: PRIMARY,
    submitLabel: "Save review",
    fields: [{ name: "reviews", label: "Reports", type: "reviews", required: true }],
    validate: (v) => {
      const decided = Object.values<any>(v.reviews || {}).filter((r) => r.decision);
      if (decided.length === 0) return "Choose Accept or Send back for at least one report maker.";
      const missing = decided.find((r) => r.decision === "reject" && !String(r.remarks || "").trim());
      return missing ? `Say why ${missing.name}'s report is sent back — they are shown the reason.` : null;
    },
    request: (id, v) => ({
      method: "post",
      url: `/designing/sites/${id}/review-reports`,
      body: {
        reviews: Object.entries<any>(v.reviews || {})
          .filter(([, r]) => r.decision)
          .map(([userId, r]) => ({
            report_maker_id: Number(userId),
            decision: r.decision,
            remarks: clean(String(r.remarks || "").trim()),
            qa_feedback: clean(String(r.qa_feedback || "").trim()),
            rating: r.rating || undefined,
          })),
      },
    }),
  },
  reject_report: {
    label: "Reject Report",
    title: "Reject report",
    description:
      "Sends the report back to the report makers you choose, with your remarks. Only they can upload it again; it returns to you once they do.",
    icon: FiXCircle,
    tone: DANGER,
    submitLabel: "Reject report",
    fields: [
      { name: "return_to", label: "Send back to", type: "rework_pick", required: true },
      { name: "remarks", label: "Reason for rejecting", type: "textarea", required: true, placeholder: "What has to be corrected in the report?" },
      { name: "qa_feedback", label: "QA feedback", type: "textarea" },
      { name: "rating", label: "Report maker rating", type: "rating" },
    ],
    request: (id, v) => ({
      method: "post",
      url: `/designing/sites/${id}/reject-report`,
      body: { remarks: v.remarks, qa_feedback: clean(v.qa_feedback), rating: v.rating || undefined, report_maker_ids: v.return_to },
    }),
    validate: (v) => (Array.isArray(v.return_to) && v.return_to.length > 0 ? null : "Choose at least one report maker to send it back to."),
  },
  send_to_client: {
    label: "Send to Client",
    title: "Mark report sent to client",
    description: "Records that the finalized report was submitted to the client. The site is completed.",
    icon: FiSend,
    tone: SUCCESS,
    submitLabel: "Mark as sent",
    fields: [
      { name: "client_email", label: "Client email (optional)", type: "email" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    request: (id, v) => ({
      method: "post",
      url: `/designing/sites/${id}/send-to-client`,
      body: { client_email: clean(v.client_email), remarks: clean(v.remarks) },
    }),
  },
  reopen: {
    label: "Reopen Site",
    title: "Reopen site",
    description:
      "The client rejected the report. The site goes back to Report Maker Assigned as a revision, on a new cycle; everything from the earlier cycle stays on the timeline.",
    icon: FiRotateCcw,
    tone: WARN,
    submitLabel: "Reopen site",
    fields: [
      { name: "reason", label: "Reason for reopening", type: "textarea", required: true, placeholder: "What did the client reject?" },
      { name: "is_billable", label: "Is this revision billable to the customer?", type: "billable", required: true },
      {
        name: "report_makers",
        label: "Report makers for the rework",
        type: "report_makers",
        required: true,
        hint: "Starts with the current report makers. Change the people or their work if the rework needs it.",
      },
    ],
    validate: reportMakersCheck,
    request: (id, v) => ({
      method: "post",
      url: `/designing/sites/${id}/reopen`,
      body: { reason: v.reason, is_billable: v.is_billable === "yes", report_makers: reportMakersBody(v.report_makers) },
    }),
  },
  hold: {
    label: "Hold",
    title: "Put site on hold",
    description: "No workflow step can be taken while the site is on hold. Resume puts it back exactly where it was.",
    icon: FiPause,
    tone: NEUTRAL,
    submitLabel: "Put on hold",
    fields: [{ name: "hold_reason", label: "Reason", type: "textarea", required: true }],
    request: (id, v) => ({ method: "put", url: `/designing/sites/${id}/hold`, body: { hold_reason: v.hold_reason } }),
  },
  resume: {
    label: "Resume",
    title: "Resume site",
    description: "Takes the site off hold, back to the stage it was parked at.",
    icon: FiPlay,
    tone: PRIMARY,
    submitLabel: "Resume site",
    fields: [{ name: "remarks", label: "Remarks", type: "textarea" }],
    request: (id, v) => ({ method: "put", url: `/designing/sites/${id}/resume`, body: { remarks: clean(v.remarks) } }),
  },
  cancel: {
    label: "Cancel Site",
    title: "Cancel site",
    description: "A cancelled site cannot be worked on again. Its Site ID becomes available for a new site in the project.",
    icon: FiXCircle,
    tone: "border border-red-200 text-red-700 hover:bg-red-50 bg-white",
    submitLabel: "Cancel site",
    fields: [{ name: "cancel_reason", label: "Reason", type: "textarea", required: true }],
    request: (id, v) => ({ method: "put", url: `/designing/sites/${id}/cancel`, body: { cancel_reason: v.cancel_reason } }),
  },
  add_files: {
    label: "Add Files",
    title: "Add files",
    description: "Adds files to a step after it was done — more site data, a corrected report. The workflow does not move.",
    icon: FiUpload,
    tone: NEUTRAL,
    submitLabel: "Upload files",
    fields: [
      {
        name: "upload_stage",
        label: "Add to",
        type: "select",
        required: true,
        options: DESIGN_FILE_STAGES.map((s) => ({ value: s.value, label: s.label })),
      },
      { name: "files", label: "Files", type: "files", required: true },
      { name: "description", label: "Description", type: "text" },
    ],
    validate: (_values, files) => (files.length === 0 ? "Choose at least one file." : null),
    request: (id, v, uploadedFiles) => ({
      method: "post",
      url: `/designing/sites/${id}/files`,
      body: { upload_stage: v.upload_stage, uploadedFiles, description: clean(v.description) },
    }),
  },
  update_billing: {
    label: "Update Billing",
    title: "Update billing",
    description: "Billing status and billing details of this Designing site.",
    icon: FaIndianRupeeSign,
    tone: NEUTRAL,
    submitLabel: "Save billing",
    fields: [
      { name: "billing_status", label: "Billing status", type: "select", required: true, options: DESIGN_BILLING_STATUSES },
      { name: "billing_invoice_number", label: "Invoice number", type: "text" },
      { name: "billing_invoice_date", label: "Invoice date", type: "date" },
      { name: "billing_invoice_amount", label: "Invoice amount", type: "number" },
      { name: "billing_details", label: "Billing details", type: "textarea" },
    ],
    request: (id, v) => ({
      method: "put",
      url: `/designing/sites/${id}/billing`,
      body: {
        billing_status: v.billing_status,
        billing_invoice_number: v.billing_invoice_number ?? "",
        billing_invoice_date: v.billing_invoice_date ?? "",
        billing_invoice_amount: v.billing_invoice_amount ?? "",
        billing_details: v.billing_details ?? "",
      },
    }),
  },
  update_vendor: {
    label: "Update Vendor",
    title: "Update vendor details",
    description: "Vendor, vendor status, vendor PO and vendor invoice details of this Designing site.",
    icon: FaIndianRupeeSign,
    tone: NEUTRAL,
    submitLabel: "Save vendor details",
    fields: [
      { name: "vendor", label: "Vendor", type: "vendor", hint: "Leave empty for work done by an in-house team." },
      { name: "vendor_status", label: "Vendor status", type: "select", required: true, options: DESIGN_VENDOR_STATUSES },
      { name: "vendor_po_number", label: "Vendor PO number", type: "text" },
      { name: "vendor_po_date", label: "Vendor PO date", type: "date" },
      { name: "vendor_invoice_number", label: "Vendor invoice number", type: "text" },
      { name: "vendor_invoice_date", label: "Vendor invoice date", type: "date" },
      { name: "vendor_invoice_amount", label: "Vendor invoice amount", type: "number" },
      { name: "vendor_invoice_details", label: "Vendor invoice details", type: "textarea" },
    ],
    request: (id, v) => ({
      method: "put",
      url: `/designing/sites/${id}/vendor`,
      body: {
        vendor_id: v.vendor?.id ?? "",
        vendor_status: v.vendor_status,
        vendor_po_number: v.vendor_po_number ?? "",
        vendor_po_date: v.vendor_po_date ?? "",
        vendor_invoice_number: v.vendor_invoice_number ?? "",
        vendor_invoice_date: v.vendor_invoice_date ?? "",
        vendor_invoice_amount: v.vendor_invoice_amount ?? "",
        vendor_invoice_details: v.vendor_invoice_details ?? "",
      },
    }),
  },
};

// The header shows the step actions first, then the status actions.
const HEADER_ACTION_ORDER = [
  "upload_data",
  "assign_report_maker",
  "upload_report",
  "reject_site_data",
  "assign_validator",
  "review_reports",
  "finalize_report",
  "send_to_client",
  "reopen",
  "reassign_report_maker",
  "reassign_validator",
  "resume",
  "hold",
  "cancel",
];

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600";

// ── Small display helpers ────────────────────────────────────────────────────
const Badge: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${className}`}>{children}</span>
);

// Report makers with their work; `showStatus` adds where each one's report stands.
const ReportStatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${reportMakerStatusBadgeClass(status)}`}>
    {reportMakerStatusLabel(status)}
  </span>
);

const ReportMakerList: React.FC<{ team: any[]; showStatus?: boolean }> = ({ team, showStatus }) =>
  team.length === 0 ? (
    <>Not assigned</>
  ) : (
    <ul className="space-y-1">
      {team.map((m) => (
        <li key={m.user_id}>
          <span className="font-medium">{personName(m.user) || `User ${m.user_id}`}</span>
          {showStatus && (
            <span className="ml-2">
              <ReportStatusBadge status={m.report_status} />
            </span>
          )}
          {m.work_description && <span className="block text-xs text-gray-600">{m.work_description}</span>}
        </li>
      ))}
    </ul>
  );

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="py-2 border-b border-gray-100 last:border-0">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5 break-words whitespace-pre-wrap">
      {value === 0 || value ? value : "-"}
    </div>
  </div>
);

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  icon,
  children,
  action,
}) => (
  <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
    <div className="flex items-center justify-between gap-3 mb-2">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

const Stars: React.FC<{ value?: number | null }> = ({ value }) =>
  value ? (
    <span className="inline-flex items-center gap-0.5" title={`${value} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FiStar key={n} className={`w-3.5 h-3.5 ${n <= value ? "text-amber-500 fill-amber-400" : "text-gray-300"}`} />
      ))}
    </span>
  ) : null;

const formatSize = (bytes?: number | string | null) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Labels for the keys a step's `details` object can carry.
const DETAIL_LABELS: Record<string, string> = {
  report_type: "Report type",
  is_billable: "Billable",
  priority: "Priority",
  required_date: "Required date",
  report_instruction1: "Report instruction 1",
  report_instruction2: "Report instruction 2",
  shared_via: "Shared via",
  link: "Link",
  remarks: "Remarks",
  quality_feedback: "Quality feedback",
  qa_feedback: "QA feedback",
  rating: "Rating",
  client_email: "Client email",
  rejection_reason: "Rejection reason",
  rejected_at: "Rejected on",
  report_progress: "Reports",
};

const renderDetail = (key: string, value: any): React.ReactNode => {
  if (value === null || value === undefined || value === "") return null;
  switch (key) {
    case "report_type":
      return reportTypeLabel(value);
    case "is_billable":
      return value ? "Yes" : "No";
    case "priority":
      return priorityLabel(value);
    case "required_date":
      return formatDateOnly(value);
    case "shared_via":
      return shareModeLabel(value);
    case "rating":
      return <Stars value={Number(value)} />;
    case "rejected_at":
      return formatDateTime(value);
    case "report_progress":
      return value?.total
        ? `${Number(value.submitted || 0) + Number(value.accepted || 0)} of ${value.total} in · ${value.accepted || 0} accepted${
            value.rejected ? ` · ${value.rejected} sent back` : ""
          }`
        : null;
    case "link":
      return /^https?:\/\//i.test(String(value)) ? (
        <a href={String(value)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline break-all">
          {String(value)}
        </a>
      ) : (
        String(value)
      );
    default:
      return String(value);
  }
};

// ── Files ────────────────────────────────────────────────────────────────────
const FileList: React.FC<{ files: any[]; canDelete: boolean; onDelete: (file: any) => void; showCycle: boolean }> = ({
  files,
  canDelete,
  onDelete,
  showCycle,
}) => {
  if (!files.length) return <p className="text-xs text-gray-400">No files.</p>;
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded">
      {files.map((file) => (
        <li key={file.id} className="px-3 py-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-gray-800 truncate flex items-center gap-1.5">
              <FiFileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {file.url ? (
                <a href={file.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline truncate" title={file.file_name}>
                  {file.file_name}
                </a>
              ) : (
                <span className="truncate" title="This file is no longer available in storage">
                  {file.file_name}
                </span>
              )}
              {file.url && <FiExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            </div>
            <div className="text-[11px] text-gray-500 truncate">
              {[
                formatSize(file.file_size),
                formatDateTime(file.uploaded_at),
                file.uploader ? personName(file.uploader) : "",
                showCycle ? `Cycle ${file.cycle_number}` : "",
                file.is_step_upload === false ? "Added later" : "",
                file.description || "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          {canDelete && (
            <button onClick={() => onDelete(file)} title="Remove file" className="p-2 text-red-600 hover:bg-red-50 rounded flex-shrink-0">
              <FiTrash2 className="w-4 h-4" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};

// ── Action panel (right-side offcanvas) ──────────────────────────────────────
interface ActionPanelProps {
  siteId: string;
  site: any;
  actionKey: string;
  onClose: () => void;
  onDone: () => void;
}

const ActionPanel: React.FC<ActionPanelProps> = ({ siteId, site, actionKey, onClose, onDone }) => {
  const action = ACTIONS[actionKey];
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  // Forms that edit existing values start from them; the rest start empty.
  const initial = useMemo<Record<string, any>>(() => {
    if (actionKey === "update_billing") {
      return {
        billing_status: site.billing_status || "pending",
        billing_invoice_number: site.billing_invoice_number || "",
        billing_invoice_date: site.billing_invoice_date || "",
        billing_invoice_amount: site.billing_invoice_amount ?? "",
        billing_details: site.billing_details || "",
      };
    }
    if (actionKey === "update_vendor") {
      return {
        vendor: site.vendor ? { id: site.vendor.id, value: site.vendor.id, label: site.vendor.vendor_full_name } : null,
        vendor_status: site.vendor_status || "not_applicable",
        vendor_po_number: site.vendor_po_number || "",
        vendor_po_date: site.vendor_po_date || "",
        vendor_invoice_number: site.vendor_invoice_number || "",
        vendor_invoice_date: site.vendor_invoice_date || "",
        vendor_invoice_amount: site.vendor_invoice_amount ?? "",
        vendor_invoice_details: site.vendor_invoice_details || "",
      };
    }
    if (actionKey === "assign_report_maker" || actionKey === "reassign_report_maker" || actionKey === "reopen") {
      return { report_makers: teamRows(site) };
    }
    // By default the report goes back to everyone who worked on it.
    if (actionKey === "reject_report") return { return_to: (site.report_makers || []).map((m: any) => m.user_id) };
    if (actionKey === "upload_report") {
      // A report maker submits their own; someone not on the team (super admin)
      // picks whose report it is — preselected when only one is still open.
      const team: any[] = site.report_makers || [];
      if (team.some((m) => m.is_me)) return { shared_via: "portal" };
      const open = team.filter((m) => m.report_status !== "accepted");
      return { shared_via: "portal", report_owner_required: true, report_maker_id: open.length === 1 ? open[0].user_id : undefined };
    }
    if (actionKey === "upload_data") return { shared_via: "portal" };
    if (actionKey === "review_reports") return { reviews: {} };
    if (actionKey === "add_files") return { upload_stage: DESIGN_FILE_STAGES[0].value };
    return {};
  }, [actionKey, site]);
  const [values, setValues] = useState<Record<string, any>>(initial);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  if (!action) return null;
  const set = (name: string, value: any) => setValues((prev) => ({ ...prev, [name]: value }));

  const submit = async () => {
    setError("");
    for (const field of action.fields) {
      if (!field.required || field.type === "files") continue;
      const value = values[field.name];
      const empty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && !value.trim()) ||
        (Array.isArray(value) && value.length === 0);
      if (empty) return setError(`${field.label} is required.`);
    }
    const blocked = action.validate?.(values, files);
    if (blocked) return setError(blocked);

    setBusy(true);
    setProgress(0);
    try {
      let uploadedFiles: any[] = [];
      if (files.length) {
        // Files go to S3 first: if an upload fails nothing has been recorded
        // and the step can simply be tried again.
        const folder = actionKey === "add_files" ? fileStageFolder(values.upload_stage) : action.folder || "designing/site-data";
        uploadedFiles = await uploadFilesWithPresignedPayload(
          files.map((file) => ({ file })),
          { folderName: folder, onProgress: (p) => setProgress(p.percentage) },
        );
      }
      const { method, url, body } = action.request(siteId, values, uploadedFiles);
      await api[method](url, body);
      onDone();
      onClose();
    } catch (err: any) {
      // The axios interceptor already toasts API errors; keep the reason on
      // the panel too, and surface upload failures it never sees.
      const message = err?.response?.data?.message || err?.message || "Something went wrong";
      setError(message);
      if (!err?.response) toast.error(message);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const renderField = (field: FieldDef) => {
    const value = values[field.name];
    switch (field.type) {
      case "textarea":
        return (
          <textarea
            rows={3}
            value={value || ""}
            placeholder={field.placeholder}
            onChange={(e) => set(field.name, e.target.value)}
            className={`${inputClass} resize-none`}
            maxLength={5000}
          />
        );
      case "select":
        return (
          <select value={value ?? ""} onChange={(e) => set(field.name, e.target.value)} className={inputClass}>
            {!field.required && !field.options?.some((o) => o.value === "") && <option value="">Select…</option>}
            {(field.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );
      case "files":
        return (
          <div>
            <input
              type="file"
              multiple
              accept={DESIGN_FILE_ACCEPT}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                if (picked.length) setFiles((prev) => [...prev, ...picked]);
                e.target.value = "";
              }}
              className="text-sm"
            />
            {files.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded">
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="px-2 py-1.5 flex items-center justify-between gap-2 text-xs text-gray-700">
                    <span className="truncate">
                      {file.name} <span className="text-gray-400">· {formatSize(file.size)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                      disabled={busy}
                      title="Remove"
                      className="text-red-500 disabled:opacity-50"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      case "rating":
        return (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => set(field.name, value === n ? undefined : n)}
                title={`${n} of 5`}
                className="p-1"
              >
                <FiStar className={`w-5 h-5 ${value && n <= value ? "text-amber-500 fill-amber-400" : "text-gray-300"}`} />
              </button>
            ))}
            {value ? <span className="text-xs text-gray-500 ml-1">{value} of 5</span> : <span className="text-xs text-gray-400 ml-1">Not rated</span>}
          </div>
        );
      case "billable":
        return (
          <div className="flex items-center gap-4 text-sm text-gray-800">
            {[
              { v: "yes", label: "Yes, billable" },
              { v: "no", label: "No, not billable" },
            ].map((o) => (
              <label key={o.v} className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name={field.name} checked={value === o.v} onChange={() => set(field.name, o.v)} />
                {o.label}
              </label>
            ))}
          </div>
        );
      case "user":
        return (
          <SelectWithSearch
            value={(value as SelectOption) || null}
            onChange={(option) => set(field.name, option)}
            apiEndpoint="/designing/assignable-users"
            dataKey="users"
            labelKey="first_name"
            extraParams={{ kind: field.assigneeKind || "report_maker", site_id: Number(siteId) }}
            displayFormat={(u: any) =>
              `${`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username}${u.employee_code ? ` (${u.employee_code})` : ""}`
            }
            placeholder="Search by name or employee code…"
            minSearchLength={0}
          />
        );
      case "report_makers": {
        const rows: ReportMakerRow[] = value || [];
        const update = (index: number, patch: Partial<ReportMakerRow>) =>
          set(field.name, rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
        return (
          <div className="space-y-3">
            {rows.map((row, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <SelectWithSearch
                      value={row.user}
                      onChange={(option) => update(index, { user: option })}
                      apiEndpoint="/designing/assignable-users"
                      dataKey="users"
                      labelKey="first_name"
                      extraParams={{ kind: "report_maker", site_id: Number(siteId) }}
                      displayFormat={(u: any) =>
                        `${`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username}${u.employee_code ? ` (${u.employee_code})` : ""}`
                      }
                      placeholder="Search by name or employee code…"
                      minSearchLength={0}
                    />
                  </div>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => set(field.name, rows.filter((_, i) => i !== index))}
                      title="Remove this report maker"
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={row.work}
                  onChange={(e) => update(index, { work: e.target.value })}
                  maxLength={2000}
                  placeholder="Work for this report maker (optional) — e.g. Prepare the structural drawings"
                  className={`${inputClass} mt-2`}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => set(field.name, [...rows, { user: null, work: "" }])}
              className="text-sm font-medium text-blue-800 hover:underline"
            >
              + Add another report maker
            </button>
          </div>
        );
      }
      case "rework_pick": {
        const picked: number[] = value || [];
        const team: any[] = site.report_makers || [];
        if (team.length === 0) return <p className="text-sm text-gray-500">This site has no report makers.</p>;
        return (
          <div className="space-y-2">
            {team.map((m) => (
              <label key={m.user_id} className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={picked.includes(m.user_id)}
                  onChange={(e) =>
                    set(field.name, e.target.checked ? [...picked, m.user_id] : picked.filter((id) => id !== m.user_id))
                  }
                />
                <span>
                  {personName(m.user) || `User ${m.user_id}`}
                  {m.work_description && <span className="block text-xs text-gray-500">{m.work_description}</span>}
                </span>
              </label>
            ))}
          </div>
        );
      }
      case "report_owner": {
        const team: any[] = site.report_makers || [];
        const mine = team.find((m) => m.is_me);
        if (mine) {
          return (
            <div className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
              Your report <ReportStatusBadge status={mine.report_status} />
              {mine.report_status === "rejected" && mine.review_remarks && (
                <span className="block w-full text-xs text-red-700 mt-1">Sent back: {mine.review_remarks}</span>
              )}
              {mine.report_status === "submitted" && (
                <span className="block w-full text-xs text-gray-500 mt-1">Submitting again replaces your earlier submission.</span>
              )}
            </div>
          );
        }
        const open = team.filter((m) => m.report_status !== "accepted");
        return (
          <select
            value={value ?? ""}
            onChange={(e) => set(field.name, e.target.value ? Number(e.target.value) : undefined)}
            className={inputClass}
          >
            <option value="">Select report maker…</option>
            {open.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {personName(m.user) || `User ${m.user_id}`} — {reportMakerStatusLabel(m.report_status)}
              </option>
            ))}
          </select>
        );
      }
      case "reviews": {
        const reviews: Record<string, any> = value || {};
        const team: any[] = site.report_makers || [];
        const patch = (m: any, next: Record<string, any>) =>
          set(field.name, {
            ...reviews,
            [m.user_id]: { name: personName(m.user) || `User ${m.user_id}`, ...(reviews[m.user_id] || {}), ...next },
          });
        return (
          <div className="space-y-3">
            {team.map((m) => {
              const ready = m.report_status === "submitted" || m.report_status === "accepted";
              const review = reviews[m.user_id] || {};
              const choices = [
                ...(m.report_status === "submitted" ? [{ v: "accept", label: "Accept", cls: "border-green-600 bg-green-50 text-green-800" }] : []),
                { v: "reject", label: "Send back", cls: "border-red-600 bg-red-50 text-red-700" },
                { v: "", label: m.report_status === "accepted" ? "Keep accepted" : "Decide later", cls: "border-gray-400 bg-gray-50 text-gray-700" },
              ];
              return (
                <div key={m.user_id} className={`border rounded-md p-3 ${ready ? "border-gray-200 bg-white" : "border-dashed border-gray-200 bg-gray-50"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{personName(m.user) || `User ${m.user_id}`}</div>
                      {m.work_description && <div className="text-xs text-gray-500">{m.work_description}</div>}
                    </div>
                    <ReportStatusBadge status={m.report_status} />
                  </div>
                  {ready ? (
                    <>
                      <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                        <div>
                          Submitted {formatDateTime(m.submitted_at)}
                          {Number(m.submission_count) > 1 ? ` · submission ${m.submission_count}` : ""}
                          {m.report_shared_via ? ` · ${shareModeLabel(m.report_shared_via)}` : ""}
                        </div>
                        {m.report_link && (
                          <div className="break-all">
                            Link:{" "}
                            {/^https?:\/\//i.test(m.report_link) ? (
                              <a href={m.report_link} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                                {m.report_link}
                              </a>
                            ) : (
                              m.report_link
                            )}
                          </div>
                        )}
                        {m.report_remarks && <div>Remarks: {m.report_remarks}</div>}
                        {(m.files || []).length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            {(m.files || []).map((f: any) =>
                              f.url ? (
                                <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">
                                  <FiFileText className="w-3 h-3" /> {f.file_name}
                                </a>
                              ) : (
                                <span key={f.id}>{f.file_name}</span>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {choices.map((c) => (
                          <button
                            key={c.v || "none"}
                            type="button"
                            onClick={() => patch(m, { decision: c.v || undefined })}
                            className={`px-3 py-1.5 text-xs font-medium rounded border ${
                              (review.decision || "") === c.v ? c.cls : "border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                      {review.decision && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            rows={2}
                            value={review.remarks || ""}
                            onChange={(e) => patch(m, { remarks: e.target.value })}
                            maxLength={5000}
                            placeholder={review.decision === "reject" ? "Why is it sent back? What has to be corrected? (required)" : "Remarks (optional)"}
                            className={`${inputClass} resize-none ${review.decision === "reject" && !String(review.remarks || "").trim() ? "border-red-300" : ""}`}
                          />
                          <input
                            type="text"
                            value={review.qa_feedback || ""}
                            onChange={(e) => patch(m, { qa_feedback: e.target.value })}
                            maxLength={5000}
                            placeholder="QA feedback (optional)"
                            className={inputClass}
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500 mr-1">Rating</span>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <button key={n} type="button" onClick={() => patch(m, { rating: review.rating === n ? undefined : n })} className="p-0.5" title={`${n} of 5`}>
                                <FiStar className={`w-4 h-4 ${review.rating && n <= review.rating ? "text-amber-500 fill-amber-400" : "text-gray-300"}`} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">
                      {m.report_status === "rejected" ? "Sent back — waiting for them to resubmit." : "Has not submitted a report yet."}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        );
      }
      case "vendor":
        return (
          <SelectWithSearch
            value={(value as SelectOption) || null}
            onChange={(option) => set(field.name, option)}
            apiEndpoint="/vendor/list"
            dataKey="vendors"
            labelKey="vendor_full_name"
            placeholder="Search vendor…"
            minSearchLength={0}
          />
        );
      default:
        return (
          <input
            type={field.type}
            value={value ?? ""}
            placeholder={field.placeholder}
            min={field.type === "number" ? 0 : undefined}
            step={field.type === "number" ? "0.01" : undefined}
            onChange={(e) => set(field.name, e.target.value)}
            className={inputClass}
          />
        );
    }
  };

  const Icon = action.icon;
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => !busy && onClose()} />
      <div className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-xl z-50 flex flex-col" role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{action.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {site.site_code}
                {site.site_name ? ` · ${site.site_name}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close" className="p-2 hover:bg-gray-100 rounded disabled:opacity-50">
            <FiX className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-sm text-gray-600">{action.description}</p>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          {action.fields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-600"> *</span>}
              </label>
              {renderField(field)}
              {field.hint && <p className="mt-1 text-xs text-gray-500">{field.hint}</p>}
            </div>
          ))}
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-100 font-medium disabled:opacity-50"
          >
            Close
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className={`flex-1 px-4 py-2 text-sm rounded font-medium disabled:opacity-60 ${
              action.tone === DANGER || actionKey === "cancel" ? DANGER : PRIMARY
            }`}
          >
            {busy ? (files.length && progress < 100 ? `Uploading ${progress}%` : "Saving…") : action.submitLabel}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Report makers: where each one's report stands ───────────────────────────
const uploadReportLabel = (mine?: any) => {
  if (!mine) return "Submit Report";
  if (mine.report_status === "rejected") return "Resubmit My Report";
  if (mine.report_status === "submitted") return "Update My Report";
  return "Submit My Report";
};

const ReportMakersPanel: React.FC<{
  site: any;
  progress?: { total: number; pending: number; submitted: number; accepted: number; rejected: number };
  canFinalize: boolean;
  available: string[];
  onAction: (key: string) => void;
  canDeleteFiles: boolean;
  onDeleteFile: (file: any) => void;
  multiCycle: boolean;
}> = ({ site, progress, canFinalize, available, onAction, canDeleteFiles, onDeleteFile, multiCycle }) => {
  const team: any[] = site.report_makers || [];
  const p = progress || { total: team.length, pending: 0, submitted: 0, accepted: 0, rejected: 0 };
  const total = Math.max(1, p.total);
  const stage = site.workflow_status;
  const inStage = ["report_maker_assigned", "report_completed", "validator_assigned"].includes(stage);
  const waitingOn = team.filter((m) => m.report_status === "pending" || m.report_status === "rejected");
  const mine = team.find((m) => m.is_me);

  let note: { tone: string; text: string } | null = null;
  if (stage === "report_maker_assigned" && waitingOn.length) {
    note = {
      tone: "bg-blue-50 border-blue-200 text-blue-900",
      text: `Waiting on ${waitingOn.map((m) => personName(m.user) || `User ${m.user_id}`).join(", ")}. The site moves on once every report maker has submitted.`,
    };
  } else if (stage === "validator_assigned" && !canFinalize) {
    note = {
      tone: "bg-amber-50 border-amber-200 text-amber-900",
      text: `${p.accepted} of ${p.total} reports accepted. The report can be finalized once the validator accepts every report maker's report.`,
    };
  } else if (stage === "validator_assigned" && canFinalize) {
    note = { tone: "bg-green-50 border-green-200 text-green-900", text: "Every report is accepted — the report can be finalized." };
  }

  return (
    <Section
      title={`Report Makers (${team.length})`}
      icon={<FiUserCheck className="text-blue-700" />}
      action={
        <div className="flex flex-wrap gap-2">
          {available.includes("upload_report") && (
            <button onClick={() => onAction("upload_report")} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded ${PRIMARY}`}>
              <FiUpload className="w-3.5 h-3.5" /> {uploadReportLabel(mine)}
            </button>
          )}
          {available.includes("review_reports") && (
            <button onClick={() => onAction("review_reports")} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded ${PRIMARY}`}>
              <FiCheckSquare className="w-3.5 h-3.5" /> Review Reports
            </button>
          )}
        </div>
      }
    >
      {inStage && (
        <div className="mb-3">
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-100" title={`${p.accepted} accepted, ${p.submitted} submitted, ${p.rejected} sent back, ${p.pending} pending`}>
            <div className="bg-green-500" style={{ width: `${(p.accepted / total) * 100}%` }} />
            <div className="bg-blue-500" style={{ width: `${(p.submitted / total) * 100}%` }} />
            <div className="bg-red-400" style={{ width: `${(p.rejected / total) * 100}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-600">
            <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />{p.accepted} accepted</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />{p.submitted} submitted</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1" />{p.rejected} sent back</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-1" />{p.pending} pending</span>
          </div>
        </div>
      )}
      {note && <p className={`mb-3 text-sm rounded border px-3 py-2 ${note.tone}`}>{note.text}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {team.map((m) => (
          <div key={m.user_id} className={`border rounded-md p-3 ${m.is_me ? "border-blue-300 bg-blue-50/30" : "border-gray-200"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {personName(m.user) || `User ${m.user_id}`}
                  {m.is_me && <span className="ml-1 text-xs font-normal text-blue-700">(you)</span>}
                </div>
                {m.work_description && <div className="text-xs text-gray-600 mt-0.5">{m.work_description}</div>}
              </div>
              <ReportStatusBadge status={m.report_status} />
            </div>
            <div className="mt-2 text-xs text-gray-600 space-y-0.5">
              {m.submitted_at ? (
                <div>
                  Submitted {formatDateTime(m.submitted_at)}
                  {Number(m.submission_count) > 1 ? ` · submission ${m.submission_count}` : ""}
                  {m.report_shared_via ? ` · ${shareModeLabel(m.report_shared_via)}` : ""}
                </div>
              ) : (
                <div className="text-gray-400">No report submitted yet</div>
              )}
              {m.report_link && (
                <div className="break-all">
                  Link:{" "}
                  {/^https?:\/\//i.test(m.report_link) ? (
                    <a href={m.report_link} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                      {m.report_link}
                    </a>
                  ) : (
                    m.report_link
                  )}
                </div>
              )}
              {m.report_remarks && <div>Remarks: {m.report_remarks}</div>}
              {m.report_quality_feedback && <div>Quality feedback: {m.report_quality_feedback}</div>}
            </div>
            {(m.files || []).length > 0 && (
              <div className="mt-2">
                <FileList files={m.files} canDelete={canDeleteFiles} onDelete={onDeleteFile} showCycle={multiCycle} />
              </div>
            )}
            {m.reviewed_at && (m.report_status === "accepted" || m.report_status === "rejected") && (
              <div
                className={`mt-2 text-xs rounded px-2 py-1.5 ${
                  m.report_status === "accepted" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-800"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {m.report_status === "accepted" ? "Accepted" : "Sent back"}
                  {m.reviewer ? ` by ${personName(m.reviewer)}` : ""} · {formatDateTime(m.reviewed_at)}
                  <Stars value={m.review_rating} />
                </div>
                {m.review_remarks && <div className="mt-0.5 whitespace-pre-wrap break-words">{m.review_remarks}</div>}
                {m.review_qa_feedback && <div className="mt-0.5">QA feedback: {m.review_qa_feedback}</div>}
              </div>
            )}
            {Number(m.rejection_count) > 0 && m.report_status !== "rejected" && (
              <div className="mt-1 text-[11px] text-gray-500">Sent back {m.rejection_count} time(s) this cycle</div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
};

// ── History tab ──────────────────────────────────────────────────────────────
const ACTION_LABELS: Record<string, string> = {
  create: "Site created",
  import: "Site imported",
  edit: "Site edited",
  data_upload: "Site data uploaded",
  assign_report_maker: "Report maker assigned",
  reassign_report_maker: "Report maker changed",
  reject_site_data: "Site data rejected",
  report_upload: "Report uploaded",
  assign_validator: "Validator assigned",
  reassign_validator: "Validator changed",
  reject_report: "Report sent back",
  accept_report: "Report accepted",
  finalize_report: "Report finalized",
  send_to_client: "Report sent to client",
  reopen: "Site reopened",
  cancel: "Site cancelled",
  hold: "Site put on hold",
  resume: "Site resumed",
  file_upload: "Files added",
  file_delete: "File removed",
  billing_update: "Billing updated",
  vendor_update: "Vendor details updated",
};

const HistoryTab: React.FC<{ siteId: string; refreshKey: number }> = ({ siteId, refreshKey }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pagination, setPagination] = useState<PaginationType | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/designing/sites/${siteId}/workflow-history`, { params: { page, limit } })
      .then((res) => {
        if (cancelled) return;
        const body = res.data?.data || {};
        setRows(body.data || []);
        const pg = body.pagination;
        setPagination(
          pg
            ? {
                offset: (Number(pg.current_page || 1) - 1) * Number(pg.per_page || limit),
                limit: Number(pg.per_page ?? limit),
                page: Number(pg.current_page ?? 1),
                total_pages: Number(pg.total_pages ?? 1),
                total_count: Number(pg.total ?? 0),
                has_next_page: !!pg.has_next_page,
                has_prev_page: !!pg.has_prev_page,
                next_page: pg.next_page ?? null,
                prev_page: pg.prev_page ?? null,
              }
            : null,
        );
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, page, limit, refreshKey]);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!rows.length) return <p className="text-sm text-gray-500">Nothing on the timeline yet.</p>;

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200">
      <ul className="divide-y divide-gray-100">
        {rows.map((row) => {
          const moved = row.from_status !== row.to_status && row.to_status;
          const rejection = row.action === "reject_site_data" || row.action === "reject_report";
          // accept_report keeps the stage but is a decision worth a green tick.
          const accepted = row.action === "accept_report";
          return (
            <li key={row.id} className="px-4 py-3 flex items-start gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  rejection ? "bg-red-100 text-red-700" : moved || accepted ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}
              >
                {rejection ? <FiXCircle size={15} /> : moved || accepted ? <FiCheckCircle size={15} /> : <FiInfo size={15} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 flex flex-wrap items-center gap-2">
                  {row.action === "report_upload" && row.metadata?.report_maker_name
                    ? `${row.metadata.resubmission ? "Report resubmitted" : "Report submitted"} by ${row.metadata.report_maker_name}`
                    : ACTION_LABELS[row.action] || String(row.action || "").replace(/_/g, " ")}
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">CYCLE {row.cycle_number}</span>
                  {row.metadata?.automatic && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700">AUTOMATIC</span>
                  )}
                  {row.metadata?.bulk && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">BULK</span>
                  )}
                </div>
                {moved && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    {row.from_status_label || "—"} → <span className="font-medium">{row.to_status_label}</span>
                  </div>
                )}
                {row.from_site_status !== row.to_site_status && row.to_site_status && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    Work status: {row.from_site_status ? workStatusLabel(row.from_site_status) : "—"} →{" "}
                    <span className="font-medium">{workStatusLabel(row.to_site_status)}</span>
                  </div>
                )}
                {row.remarks && <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">{row.remarks}</p>}
                {Array.isArray(row.metadata?.report_makers) && row.metadata.report_makers.length > 0 ? (
                  <p className="text-xs text-gray-500 mt-0.5 break-words">
                    Report makers:{" "}
                    {row.metadata.report_makers
                      .map((m: any) => `${m.name || `User ${m.user_id}`}${m.work_description ? ` (${m.work_description})` : ""}`)
                      .join(", ")}
                  </p>
                ) : (
                  row.metadata?.assignee_name && <p className="text-xs text-gray-500 mt-0.5">Assigned to {row.metadata.assignee_name}</p>
                )}
                {Array.isArray(row.metadata?.returned_to_names) && row.metadata.returned_to_names.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">Sent back to {row.metadata.returned_to_names.join(", ")}</p>
                )}
                {row.metadata?.progress?.total > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Reports: {Number(row.metadata.progress.submitted || 0) + Number(row.metadata.progress.accepted || 0)} of{" "}
                    {row.metadata.progress.total} in · {row.metadata.progress.accepted || 0} accepted
                  </p>
                )}
                {Array.isArray(row.metadata?.file_names) && row.metadata.file_names.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5 break-words">Files: {row.metadata.file_names.join(", ")}</p>
                )}
                <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-1">
                  <FiUser size={10} /> {personName(row.performer)} · {formatDateTime(row.performed_at)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {pagination && (
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          onLimitChange={(next) => {
            setLimit(next);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
type TabKey = "workflow" | "overview" | "files" | "billing" | "vendor" | "history";

const DesignSiteView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { confirm } = useConfirm();
  const canSeeHistory = hasPermission(DESIGN_RESOURCE, DESIGN_ACTIONS.HISTORY);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  // "commercials" was the old combined Billing & Vendor tab - old links land on Billing.
  const rawTab = searchParams.get("tab");
  const tab = ((rawTab === "commercials" ? "billing" : rawTab) as TabKey) || "workflow";
  const setTab = (next: TabKey) => setSearchParams(next === "workflow" ? {} : { tab: next }, { replace: true });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/designing/sites/${id}/workflow-status`);
      const body = res.data?.data;
      setData(body);
      setNotFound(false);
      // The current step opens by default; it is what the reader came for.
      setOpenSteps((prev) => (prev.size ? prev : new Set([body?.site?.workflow_status].filter(Boolean))));
    } catch (error: any) {
      if (error?.response?.status === 404) setNotFound(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const refresh = () => {
    load();
    setRefreshKey((k) => k + 1);
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (notFound || !data?.site) {
    return (
      <div className="p-6">
        <div className="bg-white border border-gray-200 rounded p-8 text-center">
          <p className="text-sm font-medium text-gray-800">Designing site not found</p>
          <p className="text-xs text-gray-500 mt-1">It does not exist, or it is not one of your sites or projects.</p>
          <Link to="/designing/sites" className="inline-flex items-center gap-2 mt-4 text-sm text-blue-700 hover:underline">
            <FiArrowLeft /> Back to Designing sites
          </Link>
        </div>
      </div>
    );
  }

  const site = data.site;
  const steps: any[] = data.steps || [];
  const rejections: any[] = data.rejections || [];
  const available: string[] = data.available_actions || [];
  // Separate permissions: DesignSite.view_billing and DesignSite.view_vendor.
  const canSeeBilling = !!data.can_view_billing;
  const canSeeVendor = !!data.can_view_vendor;
  const canDeleteFiles = available.includes("delete_files");
  const allFiles: any[] = steps.flatMap((step) => step.files || []);
  const multiCycle = Number(data.cycle_number || 1) > 1;

  const headerActions = HEADER_ACTION_ORDER.filter((key) => available.includes(key));

  const deleteFile = async (file: any) => {
    const ok = await confirm({
      title: "Remove file?",
      message: `"${file.file_name}" will be removed from this site. The timeline keeps a record that it was here.`,
      confirmText: "Remove file",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/designing/sites/${id}/files/${file.id}`);
      refresh();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  const toggleStep = (status: string) =>
    setOpenSteps((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  const tabs: Array<{ key: TabKey; label: string; icon: React.ComponentType<any>; show: boolean }> = [
    { key: "workflow", label: "Workflow", icon: FiActivity, show: true },
    { key: "overview", label: "Overview", icon: FiInfo, show: true },
    { key: "files", label: `Files (${allFiles.length})`, icon: FiFileText, show: true },
    { key: "billing", label: "Billing", icon: FaIndianRupeeSign, show: canSeeBilling },
    { key: "vendor", label: "Vendor", icon: FaIndianRupeeSign, show: canSeeVendor },
    { key: "history", label: "History", icon: FiList, show: canSeeHistory },
  ];
  const activeTab: TabKey = tabs.find((t) => t.key === tab && t.show) ? tab : "workflow";

  const statusNote =
    site.status === "hold"
      ? { tone: "border-amber-200 bg-amber-50 text-amber-900", text: `On hold since ${formatDateTime(site.held_at)}${site.hold_reason ? ` — ${site.hold_reason}` : ""}` }
      : site.status === "cancelled"
        ? { tone: "border-red-200 bg-red-50 text-red-800", text: `Cancelled ${formatDateTime(site.cancelled_at)}${site.cancel_reason ? ` — ${site.cancel_reason}` : ""}` }
        : site.status === "reopened"
          ? { tone: "border-orange-200 bg-orange-50 text-orange-900", text: `Reopened ${formatDateTime(site.reopened_at)} (cycle ${site.cycle_number})${site.reopen_reason ? ` — ${site.reopen_reason}` : ""}` }
          : null;

  return (
    <div className="space-y-4">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <Link to="/designing/sites" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <FiArrowLeft /> Designing sites
            </Link>
            <h1 className="text-xl font-semibold text-gray-900 mt-1 break-words">
              {site.site_code}
              {site.site_name ? <span className="text-gray-500 font-normal"> · {site.site_name}</span> : null}
            </h1>
            <p className="text-sm text-gray-600 mt-0.5">
              {site.project?.project_name || "-"}
              {site.client ? ` · ${site.client.client_short_name || site.client.client_full_name}` : ""}
              {site.activity ? ` · ${site.activity.activity_name}` : ""}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge className={workStatusBadgeClass(site.status)}>{workStatusLabel(site.status)}</Badge>
              <Badge className={stageBadgeClass(site.workflow_status)}>{stageLabel(site.workflow_status)}</Badge>
              {multiCycle && <Badge className="bg-orange-100 text-orange-800">Cycle {site.cycle_number}</Badge>}
              <Badge className="bg-gray-100 text-gray-700">{reportTypeLabel(site.report_type)} report</Badge>
              <Badge className={site.is_billable ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}>
                {site.is_billable ? "Billable" : "Not billable"}
              </Badge>
              <Badge className={priorityBadgeClass(site.priority)}>{priorityLabel(site.priority)} priority</Badge>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <button onClick={refresh} title="Refresh" className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded ${NEUTRAL}`}>
              <FiRefreshCw className="w-4 h-4" />
            </button>
            {available.includes("edit") && (
              <Link to={`/designing/${id}/update`} className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded ${NEUTRAL}`}>
                <FiEdit2 className="w-4 h-4" /> Edit
              </Link>
            )}
            {headerActions.map((key) => {
              const action = ACTIONS[key];
              const Icon = action.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveAction(key)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded ${action.tone}`}
                >
                  <Icon className="w-4 h-4" /> {key === "upload_report" ? uploadReportLabel(data.my_report) : action.label}
                </button>
              );
            })}
          </div>
        </div>

        {statusNote && <p className={`mt-3 text-sm rounded border px-3 py-2 ${statusNote.tone}`}>{statusNote.text}</p>}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded shadow-sm border border-gray-200 px-2">
        <div className="flex flex-wrap gap-1">
          {tabs
            .filter((t) => t.show)
            .map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === key ? "border-blue-800 text-blue-800" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
        </div>
      </div>

      {/* ── Workflow ─────────────────────────────────────────────────────── */}
      {activeTab === "workflow" && (
        <div className="space-y-4">
          {(site.report_makers || []).length > 0 && (
            <ReportMakersPanel
              site={site}
              progress={data.report_progress}
              canFinalize={!!data.can_finalize}
              available={available}
              onAction={setActiveAction}
              canDeleteFiles={canDeleteFiles}
              onDeleteFile={deleteFile}
              multiCycle={multiCycle}
            />
          )}
          <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-base font-semibold text-gray-900">Workflow Progress</h3>
              <p className="text-xs text-gray-500">
                {data.summary?.completed_steps ?? 0} of {data.summary?.total_steps ?? steps.length} steps done
                {data.summary?.rejection_count ? ` · ${data.summary.rejection_count} rejection(s)` : ""}
              </p>
            </div>
            <div className="space-y-3">
              {steps.map((step) => {
                const completed = step.state === "completed";
                const current = step.state === "current";
                const open = openSteps.has(step.status);
                const details = Object.entries(step.details || {})
                  .map(([key, value]) => ({ key, node: renderDetail(key, value) }))
                  .filter((d) => d.node !== null);
                const who = step.performed_by || step.assignee;
                return (
                  <div key={step.status} className={`border rounded overflow-hidden ${current ? "border-blue-300 bg-blue-50/40" : "border-gray-200"}`}>
                    <button onClick={() => toggleStep(step.status)} className="w-full flex items-center gap-3 p-3 text-left">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          completed ? "bg-green-100 text-green-700" : current ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {completed ? <FiCheckCircle size={16} /> : current ? <FiActivity size={16} /> : <FiClock size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${completed || current ? "text-gray-900" : "text-gray-500"}`}>
                          {step.step_number}. {step.label}
                        </p>
                        {(who || step.completed_at) && (
                          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                            {who && (
                              <>
                                <FiUser className="inline" size={10} /> {personName(who)}
                              </>
                            )}
                            {step.completed_at && <span>· {formatDateTime(step.completed_at)}</span>}
                            {step.files?.length ? <span>· {step.files.length} file(s)</span> : null}
                          </p>
                        )}
                      </div>
                      {current && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-900 text-xs font-medium rounded-full whitespace-nowrap">Current</span>
                      )}
                    </button>
                    {open && (details.length > 0 || (step.files || []).length > 0 || step.assignee || (step.assignees || []).length > 0) && (
                      <div className="px-4 pb-4 pt-1 border-t border-gray-100 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                          {Array.isArray(step.assignees) ? (
                            <DetailRow label="Report makers" value={<ReportMakerList team={step.assignees} showStatus />} />
                          ) : (
                            step.assignee && <DetailRow label="Assigned to" value={personName(step.assignee)} />
                          )}
                          {details.map((d) => (
                            <DetailRow key={d.key} label={DETAIL_LABELS[d.key] || d.key.replace(/_/g, " ")} value={d.node} />
                          ))}
                        </div>
                        {(step.files || []).length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium text-gray-500 mb-1">Files</div>
                            <FileList files={step.files} canDelete={canDeleteFiles} onDelete={deleteFile} showCycle={multiCycle} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {rejections.length > 0 && (
            <Section title={`Rejections (${rejections.length})`} icon={<FiXCircle className="text-red-600" />}>
              <ul className="divide-y divide-gray-100">
                {rejections.map((r) => (
                  <li key={r.id} className="py-2">
                    <div className="text-sm text-gray-900">
                      {r.action === "reject_site_data" ? "Site data rejected by the report maker" : "Report sent back by the validator"}
                      {multiCycle ? <span className="text-xs text-gray-500"> · cycle {r.cycle_number}</span> : null}
                    </div>
                    {Array.isArray(r.metadata?.reviews) && r.metadata.reviews.length > 0 ? (
                      <ul className="mt-1 space-y-0.5">
                        {r.metadata.reviews.map((rv: any) => (
                          <li key={rv.user_id} className="text-sm text-gray-700 break-words">
                            <span className="font-medium">{rv.name}</span>: {rv.remarks || "-"}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap break-words">{r.reason || "-"}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {personName(r.rejected_by)} · {formatDateTime(r.rejected_at)}
                    </p>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {/* ── Overview ─────────────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Site" icon={<FiInfo className="text-blue-700" />}>
            <DetailRow label="Site ID" value={site.site_code} />
            <DetailRow label="Reference ID" value={site.reference_id} />
            <DetailRow label="Site Name" value={site.site_name} />
            <DetailRow label="Project Name" value={site.project?.project_name} />
            <DetailRow label="Activity" value={site.activity?.activity_name} />
            <DetailRow label="Client" value={site.client?.client_full_name || site.client?.client_short_name} />
            <DetailRow label="PO Number" value={site.po_number} />
            <DetailRow label="PO Date" value={formatDateOnly(site.po_date)} />
          </Section>
          <Section title="Report" icon={<FiFileText className="text-blue-700" />}>
            <DetailRow label="Report Type" value={reportTypeLabel(site.report_type)} />
            <DetailRow label="Billable" value={site.is_billable ? "Yes" : "No"} />
            <DetailRow label="Priority" value={priorityLabel(site.priority)} />
            <DetailRow label="Required Date" value={formatDateOnly(site.required_date)} />
            <DetailRow label="Report Instruction 1" value={site.report_instruction1} />
            <DetailRow label="Report Instruction 2" value={site.report_instruction2} />
          </Section>
          <Section title="People" icon={<FiUser className="text-blue-700" />}>
            <DetailRow label="Created By" value={personName(site.creator)} />
            <DetailRow
              label="Report Makers"
              value={
                (site.report_makers || []).length ? (
                  <ReportMakerList team={site.report_makers} showStatus />
                ) : site.report_maker ? (
                  personName(site.report_maker)
                ) : (
                  "Not assigned"
                )
              }
            />
            <DetailRow label="Validator" value={site.validator ? personName(site.validator) : "Not assigned"} />
          </Section>
          <Section title="Status & Dates" icon={<FiClock className="text-blue-700" />}>
            <DetailRow label="Work Completion Status" value={workStatusLabel(site.status)} />
            <DetailRow label="Site Stage" value={stageLabel(site.workflow_status)} />
            <DetailRow label="Cycle" value={site.cycle_number} />
            {steps.map((step) => (
              <DetailRow key={step.status} label={`${step.label} Date`} value={formatDateTime(step.completed_at)} />
            ))}
            <DetailRow label="Last Updated Date" value={formatDateTime(site.updated_at)} />
          </Section>
        </div>
      )}

      {/* ── Files ────────────────────────────────────────────────────────── */}
      {activeTab === "files" && (
        <div className="space-y-4">
          {available.includes("add_files") && (
            <div className="flex justify-end">
              <button onClick={() => setActiveAction("add_files")} className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded ${PRIMARY}`}>
                <FiUpload className="w-4 h-4" /> Add Files
              </button>
            </div>
          )}
          {DESIGN_FILE_STAGES.map((stage) => {
            const files = allFiles.filter((file) => file.upload_stage === stage.value);
            return (
              <Section key={stage.value} title={`${stage.label} (${files.length})`} icon={<FiFileText className="text-blue-700" />}>
                <FileList files={files} canDelete={canDeleteFiles} onDelete={deleteFile} showCycle={multiCycle} />
              </Section>
            );
          })}
        </div>
      )}

      {/* ── Billing ──────────────────────────────────────────────────────── */}
      {activeTab === "billing" && canSeeBilling && (
        <div className="max-w-3xl">
          <Section
            title="Billing"
            icon={<FaIndianRupeeSign className="text-green-700" />}
            action={
              available.includes("update_billing") ? (
                <button onClick={() => setActiveAction("update_billing")} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded ${NEUTRAL}`}>
                  <FiEdit2 className="w-3.5 h-3.5" /> Update
                </button>
              ) : undefined
            }
          >
            <DetailRow label="Billing Status" value={billingStatusLabel(site.billing_status)} />
            <DetailRow label="Invoice Number" value={site.billing_invoice_number} />
            <DetailRow label="Invoice Date" value={formatDateOnly(site.billing_invoice_date)} />
            <DetailRow label="Invoice Amount" value={formatAmount(site.billing_invoice_amount)} />
            <DetailRow label="Billing Details" value={site.billing_details} />
            <DetailRow label="Last Updated" value={formatDateTime(site.billing_updated_at)} />
          </Section>
        </div>
      )}

      {/* ── Vendor ───────────────────────────────────────────────────────── */}
      {activeTab === "vendor" && canSeeVendor && (
        <div className="max-w-3xl">
          <Section
            title="Vendor"
            icon={<FaIndianRupeeSign className="text-green-700" />}
            action={
              available.includes("update_vendor") ? (
                <button onClick={() => setActiveAction("update_vendor")} className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded ${NEUTRAL}`}>
                  <FiEdit2 className="w-3.5 h-3.5" /> Update
                </button>
              ) : undefined
            }
          >
            <DetailRow label="Vendor" value={site.vendor?.vendor_full_name || "In-house"} />
            <DetailRow label="Vendor Status" value={vendorStatusLabel(site.vendor_status)} />
            <DetailRow label="Vendor PO Number" value={site.vendor_po_number} />
            <DetailRow label="Vendor PO Date" value={formatDateOnly(site.vendor_po_date)} />
            <DetailRow label="Vendor Invoice Number" value={site.vendor_invoice_number} />
            <DetailRow label="Vendor Invoice Date" value={formatDateOnly(site.vendor_invoice_date)} />
            <DetailRow label="Vendor Invoice Amount" value={formatAmount(site.vendor_invoice_amount)} />
            <DetailRow label="Vendor Invoice Details" value={site.vendor_invoice_details} />
            <DetailRow label="Last Updated" value={formatDateTime(site.vendor_updated_at)} />
          </Section>
        </div>
      )}

      {/* ── History ──────────────────────────────────────────────────────── */}
      {activeTab === "history" && canSeeHistory && <HistoryTab siteId={id} refreshKey={refreshKey} />}

      {activeAction && (
        <ActionPanel
          // Re-mounted per action so one form's values never leak into the next.
          key={activeAction}
          siteId={id}
          site={site}
          actionKey={activeAction}
          onClose={() => setActiveAction(null)}
          onDone={refresh}
        />
      )}
    </div>
  );
};

export default DesignSiteView;
